import { AuthInterceptorService } from './auth-interceptor.service';

/**
 * Session Monitor Service
 * 
 * Monitors user activity and manages session lifecycle:
 * - Tracks user activity (mouse, keyboard, API calls)
 * - Automatically refreshes tokens for active users
 * - Detects idle timeout and logs out inactive users
 * - Provides session expiry warnings
 * - Manages session state across tabs
 */
export class SessionMonitorService {
  // Configuration
  private readonly IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours of inactivity
  private readonly ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute
  private readonly TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // Refresh every 5 minutes for active users
  private readonly SESSION_WARNING_THRESHOLD_MS = 2 * 60 * 1000; // Warn 2 minutes before expiry
  private readonly STORAGE_KEY = 'sessionActivity';
  private readonly CROSS_TAB_SYNC_KEY = 'sessionSync';

  // State
  private lastActivityTime: number = Date.now();
  private isUserActive: boolean = true;
  private activityCheckTimer: NodeJS.Timeout | null = null;
  private tokenRefreshTimer: NodeJS.Timeout | null = null;
  private sessionWarningTimer: NodeJS.Timeout | null = null;
  private isMonitoring: boolean = false;

  // Callbacks
  private onSessionExpiredCallback: (() => void) | null = null;
  private onIdleTimeoutCallback: (() => void) | null = null;
  private onSessionWarningCallback: ((minutesRemaining: number) => void) | null = null;
  private onActivityResumedCallback: (() => void) | null = null;

  // Dependencies
  private authInterceptor: AuthInterceptorService | null = null;

  constructor() {
    this.handleActivity = this.handleActivity.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  /**
   * Initialize the session monitor with auth interceptor
   */
  initialize(authInterceptor: AuthInterceptorService): void {
    this.authInterceptor = authInterceptor;
  }

  /**
   * Start monitoring user session
   */
  startMonitoring(): void {
    if (this.isMonitoring) {
      console.log('SessionMonitor: Already monitoring');
      return;
    }

    console.log('SessionMonitor: Starting session monitoring');
    this.isMonitoring = true;
    this.lastActivityTime = Date.now();
    this.isUserActive = true;

    // Update storage with initial activity
    this.updateActivityStorage();

    // Add activity listeners
    this.addActivityListeners();

    // Start periodic checks
    this.startActivityCheck();
    this.startTokenRefresh();
    this.scheduleSessionWarning();

    // Listen for cross-tab communication
    window.addEventListener('storage', this.handleStorageChange);
    
    // Listen for tab visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Handle tab close
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Stop monitoring user session
   */
  stopMonitoring(): void {
    if (!this.isMonitoring) {
      return;
    }

    console.log('SessionMonitor: Stopping session monitoring');
    this.isMonitoring = false;

    // Remove activity listeners
    this.removeActivityListeners();

    // Clear timers
    if (this.activityCheckTimer) {
      clearInterval(this.activityCheckTimer);
      this.activityCheckTimer = null;
    }
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
    if (this.sessionWarningTimer) {
      clearTimeout(this.sessionWarningTimer);
      this.sessionWarningTimer = null;
    }

    // Remove global listeners
    window.removeEventListener('storage', this.handleStorageChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Clear storage
    this.clearActivityStorage();
  }

  /**
   * Add activity event listeners
   */
  private addActivityListeners(): void {
    // User interaction events
    document.addEventListener('mousedown', this.handleActivity, { passive: true });
    document.addEventListener('mousemove', this.handleActivity, { passive: true });
    document.addEventListener('keypress', this.handleActivity, { passive: true });
    document.addEventListener('scroll', this.handleActivity, { passive: true });
    document.addEventListener('touchstart', this.handleActivity, { passive: true });
    document.addEventListener('click', this.handleActivity, { passive: true });
    
    // Focus events
    window.addEventListener('focus', this.handleActivity, { passive: true });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    document.removeEventListener('mousedown', this.handleActivity);
    document.removeEventListener('mousemove', this.handleActivity);
    document.removeEventListener('keypress', this.handleActivity);
    document.removeEventListener('scroll', this.handleActivity);
    document.removeEventListener('touchstart', this.handleActivity);
    document.removeEventListener('click', this.handleActivity);
    window.removeEventListener('focus', this.handleActivity);
  }

  /**
   * Handle user activity
   */
  private handleActivity = (() => {
    let throttleTimer: NodeJS.Timeout | null = null;
    
    return () => {
      if (!this.isMonitoring) return;
      
      // Throttle activity updates to once per second
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
      }, 1000);

      const now = Date.now();
      const wasInactive = !this.isUserActive;
      
      this.lastActivityTime = now;
      this.isUserActive = true;
      
      // Update cross-tab storage
      this.updateActivityStorage();
      
      // If user was inactive and resumed activity
      if (wasInactive) {
        console.log('SessionMonitor: User activity resumed');
        if (this.onActivityResumedCallback) {
          this.onActivityResumedCallback();
        }
        
        // Reschedule warning since user is active again
        this.scheduleSessionWarning();
      }
    };
  })();

  /**
   * Record API activity (called from outside when API calls are made)
   */
  recordApiActivity(): void {
    this.handleActivity();
  }

  /**
   * Start periodic activity check
   */
  private startActivityCheck(): void {
    this.activityCheckTimer = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - this.lastActivityTime;
      const otherTabActivity = this.getLastActivityFromStorage();
      
      // Use the most recent activity across all tabs
      const mostRecentActivity = Math.max(this.lastActivityTime, otherTabActivity);
      const actualTimeSinceActivity = now - mostRecentActivity;

      // Check for idle timeout
      if (actualTimeSinceActivity > this.IDLE_TIMEOUT_MS) {
        console.log('SessionMonitor: Idle timeout reached');
        this.isUserActive = false;
        
        if (this.onIdleTimeoutCallback) {
          this.onIdleTimeoutCallback();
        }
        
        // Stop monitoring after idle timeout
        this.stopMonitoring();
      } else if (actualTimeSinceActivity > this.IDLE_TIMEOUT_MS / 2) {
        // Mark as inactive if halfway to timeout
        this.isUserActive = false;
      }
    }, this.ACTIVITY_CHECK_INTERVAL_MS);
  }

  /**
   * Start automatic token refresh for active users
   */
  private startTokenRefresh(): void {
    this.tokenRefreshTimer = setInterval(async () => {
      if (!this.isUserActive || !this.authInterceptor) {
        console.log('SessionMonitor: Skipping token refresh - user inactive or no auth interceptor');
        return;
      }
      
      const token = this.authInterceptor.getAccessToken();
      if (!token) {
        console.log('SessionMonitor: No token to refresh');
        return;
      }
      
      // Check if token needs refresh
      if (this.isTokenNearExpiration(token)) {
        console.log('SessionMonitor: Refreshing token for active user');
        try {
          const newToken = await this.authInterceptor.refreshAccessToken();
          if (newToken) {
            console.log('SessionMonitor: Token refreshed successfully');
            // Reschedule session warning with new token
            this.scheduleSessionWarning();
          } else {
            console.warn('SessionMonitor: Token refresh failed');
            if (this.onSessionExpiredCallback) {
              this.onSessionExpiredCallback();
            }
          }
        } catch (error) {
          console.error('SessionMonitor: Error refreshing token:', error);
        }
      }
    }, this.TOKEN_REFRESH_INTERVAL_MS);
  }

  /**
   * Schedule session expiry warning
   */
  private scheduleSessionWarning(): void {
    // Clear existing warning timer
    if (this.sessionWarningTimer) {
      clearTimeout(this.sessionWarningTimer);
      this.sessionWarningTimer = null;
    }

    if (!this.authInterceptor) return;

    const token = this.authInterceptor.getAccessToken();
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;
      const timeUntilWarning = timeUntilExpiry - this.SESSION_WARNING_THRESHOLD_MS;

      if (timeUntilWarning > 0) {
        console.log(`SessionMonitor: Scheduling warning in ${Math.round(timeUntilWarning / 1000)}s`);

        this.sessionWarningTimer = setTimeout(() => {
          // Check if user has been active recently before showing warning
          const timeSinceActivity = Date.now() - this.lastActivityTime;
          const shouldRefreshInstead = timeSinceActivity < this.TOKEN_REFRESH_INTERVAL_MS;

          if (shouldRefreshInstead && this.authInterceptor) {
            // User has been active - refresh token instead of showing warning
            console.log('SessionMonitor: User active, refreshing token instead of showing warning');
            this.authInterceptor.refreshAccessToken()
              .then((newToken) => {
                if (newToken) {
                  console.log('SessionMonitor: Token refreshed successfully, warning suppressed');
                  // Reschedule warning with new token
                  this.scheduleSessionWarning();
                } else {
                  // Refresh failed, show warning
                  console.warn('SessionMonitor: Token refresh failed, showing warning');
                  if (this.onSessionWarningCallback) {
                    const minutesRemaining = Math.ceil(this.SESSION_WARNING_THRESHOLD_MS / 60000);
                    this.onSessionWarningCallback(minutesRemaining);
                  }
                }
              })
              .catch((error) => {
                console.error('SessionMonitor: Error refreshing token:', error);
                // Show warning on error
                if (this.onSessionWarningCallback) {
                  const minutesRemaining = Math.ceil(this.SESSION_WARNING_THRESHOLD_MS / 60000);
                  this.onSessionWarningCallback(minutesRemaining);
                }
              });
          } else if (this.isUserActive && this.onSessionWarningCallback) {
            // User is currently active but hasn't been active enough to auto-refresh
            const minutesRemaining = Math.ceil(this.SESSION_WARNING_THRESHOLD_MS / 60000);
            console.log(`SessionMonitor: Warning - session expires in ${minutesRemaining} minutes`);
            this.onSessionWarningCallback(minutesRemaining);
          }
        }, timeUntilWarning);
      }
    } catch (error) {
      console.warn('SessionMonitor: Error parsing token for warning:', error);
    }
  }

  /**
   * Check if token is near expiration
   */
  private isTokenNearExpiration(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      const eightMinutes = 8 * 60 * 1000;

      return (exp - now) < eightMinutes;
    } catch (error) {
      console.warn('SessionMonitor: Error checking token expiration:', error);
      return false;
    }
  }

  /**
   * Handle storage changes from other tabs
   */
  private handleStorageChange(event: StorageEvent): void {
    if (event.key === this.STORAGE_KEY && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        // Update our last activity time if another tab is more recent
        if (data.timestamp > this.lastActivityTime) {
          this.lastActivityTime = data.timestamp;
          this.isUserActive = true;
        }
      } catch (error) {
        console.warn('SessionMonitor: Error parsing storage event:', error);
      }
    } else if (event.key === this.CROSS_TAB_SYNC_KEY && event.newValue) {
      // Handle cross-tab sync events (logout, refresh, etc.)
      try {
        const data = JSON.parse(event.newValue);
        if (data.action === 'logout') {
          console.log('SessionMonitor: Logout detected in another tab');
          this.stopMonitoring();
          if (this.onSessionExpiredCallback) {
            this.onSessionExpiredCallback();
          }
        }
      } catch (error) {
        console.warn('SessionMonitor: Error parsing sync event:', error);
      }
    }
  }

  /**
   * Handle tab visibility changes
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      console.log('SessionMonitor: Tab became visible');
      this.handleActivity();
      
      // Check if we need to refresh token after tab was hidden
      if (this.authInterceptor) {
        const token = this.authInterceptor.getAccessToken();
        if (token && this.isTokenNearExpiration(token)) {
          console.log('SessionMonitor: Refreshing token after tab became visible');
          this.authInterceptor.refreshAccessToken();
        }
      }
    }
  }

  /**
   * Handle before unload
   */
  private handleBeforeUnload(): void {
    // Update activity one last time
    this.updateActivityStorage();
  }

  /**
   * Update activity in storage for cross-tab sync
   */
  private updateActivityStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        timestamp: this.lastActivityTime,
        active: this.isUserActive,
        tabId: this.getTabId()
      }));
    } catch (error) {
      console.warn('SessionMonitor: Error updating storage:', error);
    }
  }

  /**
   * Get last activity from storage
   */
  private getLastActivityFromStorage(): number {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.timestamp || 0;
      }
    } catch (error) {
      console.warn('SessionMonitor: Error reading storage:', error);
    }
    return 0;
  }

  /**
   * Clear activity storage
   */
  private clearActivityStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('SessionMonitor: Error clearing storage:', error);
    }
  }

  /**
   * Broadcast action to other tabs
   */
  broadcastAction(action: string, data?: any): void {
    try {
      localStorage.setItem(this.CROSS_TAB_SYNC_KEY, JSON.stringify({
        action,
        data,
        timestamp: Date.now(),
        tabId: this.getTabId()
      }));
    } catch (error) {
      console.warn('SessionMonitor: Error broadcasting action:', error);
    }
  }

  /**
   * Get unique tab ID
   */
  private getTabId(): string {
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', Math.random().toString(36).substr(2, 9));
    }
    return sessionStorage.getItem('tabId')!;
  }

  /**
   * Set session expired callback
   */
  onSessionExpired(callback: () => void): void {
    this.onSessionExpiredCallback = callback;
  }

  /**
   * Set idle timeout callback
   */
  onIdleTimeout(callback: () => void): void {
    this.onIdleTimeoutCallback = callback;
  }

  /**
   * Set session warning callback
   */
  onSessionWarning(callback: (minutesRemaining: number) => void): void {
    this.onSessionWarningCallback = callback;
  }

  /**
   * Set activity resumed callback
   */
  onActivityResumed(callback: () => void): void {
    this.onActivityResumedCallback = callback;
  }

  /**
   * Get session status
   */
  getSessionStatus(): {
    isActive: boolean;
    lastActivity: number;
    secondsSinceActivity: number;
    isMonitoring: boolean;
  } {
    const now = Date.now();
    return {
      isActive: this.isUserActive,
      lastActivity: this.lastActivityTime,
      secondsSinceActivity: Math.round((now - this.lastActivityTime) / 1000),
      isMonitoring: this.isMonitoring
    };
  }

  /**
   * Manually trigger activity (for programmatic activity like API calls)
   */
  triggerActivity(): void {
    this.handleActivity();
  }
}

// Export singleton instance
export const sessionMonitorService = new SessionMonitorService();